// WPAA Academy — guides + verified tax updates registry.
// Add a new guide or update entry here and it appears on the hub.
// URLs: /knowledge-academy/<slug>

export const knowledgeGuides = [
  {
    slug: 'uae-vat-guide-2026',
    category: 'VAT',
    title: 'UAE VAT Guide',
    subtitle: 'Everything Businesses Need to Know About VAT in the UAE',
    metaTitle: 'UAE VAT Guide | Registration, Returns & Compliance | WPAA',
    metaDescription:
      'A practical guide to UAE VAT for businesses: registration thresholds, taxable vs zero-rated vs exempt supplies, input tax recovery, reverse charge, returns and penalties.',
    excerpt:
      'Understand UAE VAT in simple terms: registration thresholds, supply categories, input tax recovery, reverse charge mechanism, returns and penalties.',
    readingTime: '10 min read',
    status: 'published',
    tags: ['VAT', 'FTA', 'Compliance']
  },
  {
    slug: 'uae-corporate-tax-guide-2026',
    category: 'Corporate Tax',
    title: 'UAE Corporate Tax Guide',
    subtitle: 'A Practical Guide for Businesses Operating in the UAE',
    metaTitle: 'UAE Corporate Tax Guide | Registration, Rates & Filing | WPAA',
    metaDescription:
      'A practical guide to UAE Corporate Tax: 0% and 9% rates, registration, taxable income, deductible expenses, Small Business Relief, Free Zone rules and transfer pricing.',
    excerpt:
      'Understand UAE Corporate Tax: 0% and 9% rates, registration, deductible expenses, Small Business Relief, Free Zone qualifying rules and transfer pricing.',
    readingTime: '9 min read',
    status: 'published',
    tags: ['Corporate Tax', 'FTA', 'Compliance']
  },
  {
    slug: 'uae-customs-duty-guide',
    category: 'Customs Duty',
    title: 'UAE Customs Duty Guide',
    subtitle: 'Understanding Import Duties, Customs Procedures and Compliance',
    metaTitle: 'UAE Customs Duty Guide | Import Duties, HS Codes & Procedures | WPAA',
    metaDescription:
      'A practical guide to UAE Customs Duty: CIF valuation, HS codes, import documentation, mainland vs free-zone imports, and common customs compliance mistakes.',
    excerpt:
      'Learn how UAE customs duty is calculated, HS Codes, import procedures, CIF valuation, free-zone movements and customs compliance essentials.',
    readingTime: '6 min read',
    status: 'published',
    tags: ['Customs', 'Import', 'Compliance']
  },
  {
    slug: 'uae-goaml-aml-compliance-guide',
    category: 'goAML / AML',
    title: 'UAE goAML & AML Compliance Guide',
    subtitle: 'A Practical Guide to Anti-Money Laundering Compliance for UAE Businesses',
    metaTitle: 'UAE goAML & AML Compliance Guide | DNFBP, CDD, EDD, STR | WPAA',
    metaDescription:
      'A practical guide to UAE goAML and AML compliance: DNFBP registration, CDD, EDD, suspicious transaction reporting, urgent PSTRs and UAEFIU orders under Regulation No. 1 of 2026, and internal audits.',
    excerpt:
      'Understand goAML registration, CDD, EDD, STR reporting, AML policies, risk assessments and internal audit requirements for UAE DNFBPs.',
    readingTime: '9 min read',
    updated: '2026-11-02',
    status: 'published',
    tags: ['goAML', 'AML', 'DNFBP', 'Compliance', 'UAEFIU', 'PSTR']
  },
  {
    slug: 'vat-transfer-business-uae-togc',
    category: 'VAT',
    title: 'UAE VAT on Business Transfers',
    subtitle: 'When Does Transfer of a Going Concern (TOGC) Treatment Apply?',
    metaTitle: 'UAE VAT on Business Transfers: TOGC Guide | WPAA',
    metaDescription:
      'Buying or selling a UAE business? Learn when a transfer qualifies as a going concern, when VAT applies, and what buyers and sellers should document.',
    excerpt:
      'A sale of a UAE business may fall outside the scope of VAT when it qualifies as a transfer of a business as a going concern. This guide explains the conditions, common failure points and records buyers and sellers should prepare before completion.',
    readingTime: '12 min read',
    status: 'published',
    tags: ['VAT', 'TOGC', 'M&A', 'Article 7']
  },
  {
    slug: 'uae-free-zones-vs-designated-zones-tax',
    category: 'Corporate Tax',
    title: 'UAE Free Zones vs Designated Zones',
    subtitle: 'Corporate Tax, VAT and Excise Tax Explained',
    metaTitle: 'UAE Free Zones vs Designated Zones: CT, VAT & Excise | WPAA',
    metaDescription:
      'Understand how UAE Free Zones and Designated Zones differ for Corporate Tax, VAT and Excise Tax, including the key checks for distributors.',
    excerpt:
      'FTA Public Clarification TAXP010 makes it clear: "Free Zone" and "Designated Zone" do not have the same meaning across Corporate Tax, VAT and Excise Tax. This guide explains each regime and the checks every Free Zone business should run before relying on the 0% rate or VAT treatment.',
    readingTime: '13 min read',
    status: 'published',
    tags: ['Corporate Tax', 'VAT', 'Excise', 'Free Zone', 'QFZP']
  },
  {
    slug: 'uae-vat-reverse-charge-metal-scrap-vatp047',
    category: 'VAT',
    title: 'UAE VAT Reverse Charge on Metal Scrap',
    subtitle: 'FTA Public Clarification VATP047 Explained',
    metaTitle: 'UAE VAT Reverse Charge on Metal Scrap | VATP047 Explained',
    metaDescription:
      'Learn how the UAE VAT Reverse Charge Mechanism applies to metal scrap transactions under FTA Public Clarification VATP047, including declarations, invoices and VAT return reporting.',
    excerpt:
      'FTA Public Clarification VATP047 explains how the Reverse Charge Mechanism applies to qualifying metal scrap transactions between VAT-registered UAE businesses. This guide covers eligible goods, resale vs processing, supplier and recipient duties, invoice wording and VAT return Boxes 1, 3, 9 and 10.',
    readingTime: '12 min read',
    status: 'published',
    tags: ['VAT', 'Reverse Charge', 'Metal Scrap', 'VATP047', 'FTA']
  },
  {
    slug: 'uae-e-invoicing-phases-pint-ae-guide',
    category: 'VAT',
    title: 'UAE E-Invoicing: Phases, Deadlines and PINT AE',
    subtitle: 'What Businesses Need to Know Before Go-Live',
    metaTitle: 'UAE E-Invoicing Phases, Deadlines & PINT AE Explained | WPAA',
    metaDescription:
      'UAE e-invoicing explained: implementation phases and ASP deadlines, who is in scope, the five-corner Peppol model, PINT AE data requirements, penalties and a readiness checklist.',
    excerpt:
      'UAE e-invoicing becomes mandatory from 1 January 2027 for businesses with revenue of AED 50 million or more, followed by smaller businesses and government entities. This guide covers each phase and ASP deadline, scope and exclusions, the five-corner model, PINT AE and a practical readiness roadmap.',
    readingTime: '11 min read',
    status: 'published',
    tags: ['VAT', 'E-Invoicing', 'PINT AE', 'Peppol', 'ASP', 'FTA']
  },
  {
    slug: 'uae-fiu-regulation-1-2026-suspicious-transactions-freezing-funds',
    category: 'goAML / AML',
    title: 'UAE FIU Regulation No. 1 of 2026: What Businesses Need to Know About Suspicious Transactions and Freezing of Funds',
    subtitle: 'PSTRs, Suspension and Freezing Orders Explained',
    metaTitle: 'UAE FIU Regulation No. 1 of 2026: PSTRs & Freezing of Funds | WPAA',
    metaDescription:
      'UAEFIU Regulation No. 1 of 2026 explained: who it applies to, Postponement Suspicious Transaction Reports (PSTRs), the AED 500,000 indicator, suspension and freezing orders, tipping-off and how to prepare.',
    excerpt:
      'The UAEFIU has issued Regulation No. 1 of 2026 on the postponement or suspension of suspicious transactions and the freezing of funds, effective 14 December 2026. Learn when a PSTR is required, what orders the UAEFIU can issue and how financial institutions, DNFBPs and VASPs should prepare.',
    readingTime: '8 min read',
    status: 'published',
    tags: ['AML', 'goAML', 'UAEFIU', 'PSTR', 'DNFBP', 'VASP']
  }
];

export const knowledgeCategories = ['VAT', 'Corporate Tax', 'Customs Duty', 'goAML / AML', 'Free Zone'];

/**
 * Latest Tax Updates.
 * Every entry must be independently verified by WPAA against official
 * legislation (FTA Federal Decree-Laws, Cabinet Decisions, Executive
 * Regulations, or Big-Four alerts) BEFORE being added here.
 *
 * Leave the array empty until entries are officially verified.
 */
export const taxUpdates = [];

export const getGuideBySlug = (slug) => knowledgeGuides.find((g) => g.slug === slug);
